import Button from '@ui/Button';
import CreditCardLogo from '@ui/CreditCardLogo';
import { formatCurrency } from '@util/index';
import { OrderDocument } from '@models/order';
import { useAuth } from 'context/AuthContext';
import React from 'react';

const PaymentSummary = ({ order }: { order: OrderDocument }) => {
  const { userDoc } = useAuth();
  function getPaymentType(type: number) {
    // 0-card 1-apple pay, 2-paypal, 3-google pay, 4-mx balance, 5-link, 6-Affirm
    switch (type) {
      case 0:
        return 'Card';
      case 1:
        return 'Apple Pay';
      case 2:
        return 'PayPal';
      case 3:
        return 'Google Pay';
      case 4:
        return 'MX Balance';
      case 5:
        return 'Link';
      case 6:
        return 'Affirm';
      default:
        return 'Card';
    }
  }

  const refundAmount = getRefundAmount(order);

  return (
    <div className="flex w-full flex-col items-start gap-[1.6rem] overflow-clip border-[1px] border-solid border-[#D9D9D9] sm:rounded-[1.5rem]">
      <div className="flex w-full justify-between  border-b-[1px] border-solid border-[#D9D9D9] bg-[#f0f2f2] p-[2rem]">
        <Header />
        {(!!userDoc?.roles?.support || !!userDoc?.roles?.admin) && (
          <Button
            text="Open in Stripe"
            height="small"
            width="small"
            target="_blank"
            href={`https://dashboard.stripe.com/payments/${order.payment_id}`}
          />
        )}
      </div>
      <div className="flex w-full flex-col gap-[1.6rem] p-[2rem]">
        <div className="flex w-full justify-between">
          <span>Payment Type</span>
          <span>{getPaymentType(order.payment?.type)}</span>
        </div>
        {order.payment?.type === 0 && order.payment.brand && (
          <div className="flex w-full justify-between">
            <span>Card Number</span>
            <span className="flex gap-[0.4rem]">
              <CreditCardLogo brand={order.payment.brand} /> ending in{' '}
              {order.payment.last4}
            </span>
          </div>
        )}

        <div className="h-[1px] w-full border-b-[1px] border-dashed" />
        <div className="flex w-full justify-between">
          <span>Subtotal</span>
          <span>{formatCurrency(order.subtotal)}</span>
        </div>
        {order.donate && (
          <div className="flex w-full justify-between font-medium text-brand-green">
            <span>R2R Donation</span>
            <span>$1.00</span>
          </div>
        )}
        <div className="flex w-full justify-between">
          <span>Shipping</span>
          <span>{formatCurrency(order.shipping_total ?? 0)}</span>
        </div>
        {!!order.tax && (
          <div className="flex w-full justify-between">
            <span>Tax</span>
            <span>{formatCurrency(order.tax)}</span>
          </div>
        )}
        {!!order.ra_fee && (
          <div className="flex w-full justify-between">
            <span>Return Assurance</span>
            <span>{formatCurrency(order.ra_fee)}</span>
          </div>
        )}
        {!!order.affirm_fee && (
          <div className="flex w-full justify-between">
            <span>Affirm Processing Fee</span>
            <span>{formatCurrency(order.affirm_fee)}</span>
          </div>
        )}

        {!!order.coupon?.off && (
          <>
            <div className="h-[1px] w-full border-b-[1px] border-dashed" />
            <div className="flex w-full justify-between">
              <div>
                <span>Discount</span>
                <span className="mx-2 text-brand-secondary">
                  ({order.coupon.code})
                </span>
              </div>
              <span className="text-brand-secondary">
                -{formatCurrency(order.coupon.off)}
              </span>
            </div>
          </>
        )}
        {!!refundAmount && (
          <>
            <div className="h-[1px] w-full border-b-[1px] border-dashed" />
            <div className="flex w-full justify-between">
              <span>Refunded</span>
              <span className="text-brand-secondary">
                -{formatCurrency(refundAmount)}
              </span>
            </div>
          </>
        )}

        <div className="h-[1px] w-full border-b-[1px] border-dashed" />

        <div className="flex w-full flex-col gap-[0.4rem]">
          <div className="flex w-full justify-between">
            <span className="text-[1.8rem] font-semibold">Total</span>
            <span className="text-[2.4rem] font-semibold">
              {formatCurrency(order.total - refundAmount)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = () => (
  <div className="flex w-full justify-between">
    <span className="text-[2.2rem] font-semibold">Payment Summary</span>
  </div>
);

function getRefundAmount(order: OrderDocument) {
  let refundAmount = 0;
  if (order.seller_arr) {
    order.seller_arr.forEach((seller) => {
      const items = order.sellers?.[seller] ?? [];
      items.forEach((item) => {
        refundAmount += item.refund_amount ?? 0;
      });
    });
  }
  return refundAmount;
}

export default PaymentSummary;
